import { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import {
  DECOR_COLOR_FAMILIES,
  DECOR_COLOR_FAMILY_SWATCH,
  DECOR_LIBRARY_CATEGORIES,
  DECOR_LIBRARY_CATEGORY_SPECS,
  libraryTags,
  matchesDecorLibraryFilter,
  type DecorColorFamily,
  type DecorLibraryCategory,
  type DecorLibraryItem,
} from '@peles/shared';
import { FilterSelect } from '../common/FilterSelect';
import { useDecorLibrary } from '../../hooks/useDecorLibrary';
import { DecorLibraryCard } from './DecorLibraryCard';
import { DecorLibraryItemModal } from './DecorLibraryItemModal';

/** Cards shown before "Show more". */
export const DECOR_LIBRARY_PAGE = 24;

export interface DecorLibraryBrowserProps {
  /** Pick: pressing a card chooses it. Manage: pressing a card edits it. */
  mode: 'pick' | 'manage';
  /** The categories on offer — a table offers its own, a bar only bars. All when absent. */
  categories?: readonly DecorLibraryCategory[];
  initialCategory?: DecorLibraryCategory | null;
  /** The piece already chosen, marked. */
  selectedId?: string | null;
  onPick?: (item: DecorLibraryItem) => void;
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The decor library as a filterable gallery — by kind, words, colour, tag
 * and vendor — with "Add a piece" always at hand. The floor plan picks from
 * it; Settings → Decor library manages it.
 */
export function DecorLibraryBrowser({
  mode,
  categories = DECOR_LIBRARY_CATEGORIES,
  initialCategory,
  selectedId,
  onPick,
}: DecorLibraryBrowserProps) {
  const library = useDecorLibrary();
  const [category, setCategory] = useState<DecorLibraryCategory | 'all'>(
    initialCategory ?? (categories.length === 1 ? categories[0] : 'all'),
  );
  const [q, setQ] = useState('');
  const [colors, setColors] = useState<DecorColorFamily[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [vendorIds, setVendorIds] = useState<string[]>([]);
  const [showRetired, setShowRetired] = useState(false);
  const [limit, setLimit] = useState(DECOR_LIBRARY_PAGE);
  const [editing, setEditing] = useState<DecorLibraryItem | 'new' | null>(null);

  const inScope = useMemo(
    () => (library.data ?? []).filter((i) => categories.includes(i.category)),
    [library.data, categories],
  );
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    inScope
      .filter((i) => i.isActive || showRetired)
      .forEach((i) => map.set(i.category, (map.get(i.category) ?? 0) + 1));
    return map;
  }, [inScope, showRetired]);
  const tagOptions = useMemo(() => libraryTags(inScope), [inScope]);
  const vendorOptions = useMemo(() => {
    const seen = new Map<string, string>();
    inScope.forEach((i) => i.vendors.forEach((v) => seen.set(v.vendorId, v.name)));
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [inScope]);

  const matches = useMemo(
    () =>
      inScope.filter((i) =>
        matchesDecorLibraryFilter(i, {
          category: category === 'all' ? null : category,
          q,
          tags,
          vendorIds,
          colors,
          includeInactive: mode === 'manage' && showRetired,
        }),
      ),
    [inScope, category, q, tags, vendorIds, colors, mode, showRetired],
  );
  const filtered = q || tags.length || vendorIds.length || colors.length;
  const resetPage = () => setLimit(DECOR_LIBRARY_PAGE);

  const tab = (id: DecorLibraryCategory | 'all', label: string, count: number) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={category === id}
      onClick={() => {
        setCategory(id);
        resetPage();
      }}
      className={`min-h-11 sm:min-h-9 shrink-0 rounded-full border px-3 text-sm whitespace-nowrap ${
        category === id
          ? 'border-gray-900 bg-gray-900 text-gray-50'
          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
      }`}
    >
      {label} <span className="opacity-70">{count}</span>
    </button>
  );

  return (
    <div className="space-y-3">
      {categories.length > 1 && (
        <div
          role="tablist"
          aria-label="Kind of piece"
          className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1"
        >
          {tab(
            'all',
            'All',
            [...counts.values()].reduce((a, b) => a + b, 0),
          )}
          {categories.map((c) => tab(c, DECOR_LIBRARY_CATEGORY_SPECS[c].label, counts.get(c) ?? 0))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[12rem] flex-1">
          <span className="sr-only">Search the library</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              resetPage();
            }}
            placeholder="Search names, tags, vendors…"
            className="w-full min-h-11 sm:min-h-9 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </label>
        <FilterSelect<DecorColorFamily>
          label="Colour"
          items={[...DECOR_COLOR_FAMILIES]}
          value={colors}
          multiple
          onChange={(v) => {
            setColors(v as DecorColorFamily[]);
            resetPage();
          }}
          getValue={(c) => c}
          getLabel={capitalise}
          renderItem={(c) => (
            <span className="flex items-center gap-2">
              <span
                className="h-4 w-4 rounded-full border border-gray-300"
                style={{ backgroundColor: DECOR_COLOR_FAMILY_SWATCH[c] }}
              />
              {capitalise(c)}
            </span>
          )}
        />
        {tagOptions.length > 0 && (
          <FilterSelect<string>
            label="Tags"
            items={tagOptions}
            value={tags}
            multiple
            searchable
            onChange={(v) => {
              setTags(v as string[]);
              resetPage();
            }}
            getValue={(t) => t}
            getLabel={(t) => t}
          />
        )}
        {vendorOptions.length > 0 && (
          <FilterSelect<{ id: string; name: string }>
            label="Vendor"
            items={vendorOptions}
            value={vendorIds}
            multiple
            searchable
            onChange={(v) => {
              setVendorIds(v as string[]);
              resetPage();
            }}
            getValue={(v) => v.id}
            getLabel={(v) => v.name}
          />
        )}
        {mode === 'manage' && (
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Show retired
          </label>
        )}
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="ml-auto inline-flex items-center gap-1.5 min-h-11 sm:min-h-9 rounded-lg bg-primary-600 px-3 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Add a piece
        </button>
      </div>

      {library.isLoading ? (
        <div
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
          data-testid="decor-library-loading"
        >
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="aspect-[4/5] animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      ) : library.isError ? (
        <p role="alert" className="text-sm text-red-600">
          The library could not be loaded.{' '}
          <button type="button" onClick={() => void library.refetch()} className="underline">
            Try again
          </button>
        </p>
      ) : matches.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          {filtered
            ? 'Nothing matches these filters.'
            : category === 'all'
              ? 'The library is empty. Add the chairs, linens and flowers you offer, with their pictures.'
              : `No ${DECOR_LIBRARY_CATEGORY_SPECS[category].label.toLowerCase()} yet.`}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {matches.slice(0, limit).map((item) => (
              <DecorLibraryCard
                key={item.id}
                item={item}
                selected={item.id === selectedId}
                pressLabel={mode === 'pick' ? 'Choose' : 'Edit'}
                onPress={(i) => (mode === 'pick' ? onPick?.(i) : setEditing(i))}
                onEdit={mode === 'pick' ? (i) => setEditing(i) : undefined}
              />
            ))}
          </div>
          {matches.length > limit && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setLimit((n) => n + DECOR_LIBRARY_PAGE)}
                className="min-h-11 sm:min-h-9 rounded-lg border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
              >
                Show more ({matches.length - limit} left)
              </button>
            </div>
          )}
        </>
      )}

      <DecorLibraryItemModal
        open={editing !== null}
        onClose={() => setEditing(null)}
        item={editing === 'new' ? null : editing}
        defaultCategory={category === 'all' ? null : category}
        onSaved={(saved) => {
          if (editing === 'new' && mode === 'pick' && categories.includes(saved.category)) {
            onPick?.(saved);
          }
        }}
      />
    </div>
  );
}
